import React from 'react'
import { AvatarCircle, Container, Attachment } from '..'
import {
    Message as MessageType,
    Attachment as AttachmentType,
    WorkSpaceUser,
    DELETE_IMAGES,
} from '../../models'
import { Tooltip, useTheme, IconButton } from '@material-ui/core'
import { getMessageTime } from '../../models/Messaging/services'
import InfoIcon from '@material-ui/icons/Info'
import DeleteIcon from '@material-ui/icons/Delete'
import { hasPermission } from '../../models/Users/services'
import { typographyStyle } from '../../theme/typographyStyle'

interface IMessageProps {
    message: MessageType
    preview?: boolean
    attachmentSize?: number
    isSecondary?: boolean
    secondaryTitle?: string
    workspaceUser?: WorkSpaceUser
    deleteImage?: (messageId: number, imageId: number) => void
}

export const Message: React.FC<IMessageProps> = ({
    message,
    preview,
    attachmentSize,
    isSecondary,
    secondaryTitle,
    workspaceUser,
    deleteImage,
}) => {
    const theme = useTheme()

    const canDeleteImage = hasPermission(workspaceUser, DELETE_IMAGES)

    const attachments = message.attachments
        ? message.attachments.map((a: AttachmentType, idx) => (
              <Container
                  key={`ATTACHMENT_${idx}`}
                  style={{ position: 'relative' }}
              >
                  <Attachment
                      attachment={a.file}
                      theme={theme}
                      size={attachmentSize}
                      uploaded={a.uploaded}
                  />
                  {deleteImage && canDeleteImage && (
                      <IconButton
                          size="small"
                          onClick={() => deleteImage(message.id, a.id)}
                          style={{
                              position: 'absolute',
                              top: -8,
                              right: -8,
                              backgroundColor: theme.palette.error.main,
                              color: theme.palette.error.contrastText,
                              width: 24,
                              height: 24,
                              boxShadow: theme.shadows[2],
                          }}
                      >
                          <DeleteIcon style={{ fontSize: 16 }} />
                      </IconButton>
                  )}
              </Container>
          ))
        : undefined

    //for styling the message box
    const messageStyle: React.CSSProperties = {
        margin: theme.spacing(0, 1, 1, 1),
        padding: theme.spacing(0.5),
        backgroundColor: theme.palette.grey[200],
        boxShadow: theme.shadows[1],
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
    }

    if (preview) {
        messageStyle.maxHeight = 100
        messageStyle.overflow = 'hidden'
    }

    const displayDate = getMessageTime(new Date(message.created_date))
    return (
        <Container style={messageStyle}>
            <AvatarCircle
                image={message.sender_profile_picture}
                styles={{ container: { alignSelf: 'flex-start' } }}
                text={message.sender_name}
                initials
            />

            <Container
                direction="column"
                alignItems="flex-start"
                style={{ marginLeft: theme.spacing(1) }}
            >
                <Container style={{ marginRight: theme.spacing(2) }}>
                    <span
                        style={{
                            marginRight: theme.spacing(1),
                            ...typographyStyle(theme.typography.body1),
                            fontWeight: 700,
                        }}
                    >
                        {message.sender_name}
                    </span>
                    <span
                        style={{
                            ...typographyStyle(theme.typography.caption),
                            marginTop: theme.spacing(0.25),
                        }}
                    >
                        {displayDate}
                    </span>
                </Container>
                <Container
                    style={{ marginRight: theme.spacing(1), flexWrap: 'wrap' }}
                >
                    {attachments}
                </Container>
                <Container
                    style={{ alignSelf: 'flex-start', textAlign: 'left' }}
                >
                    <div
                        dangerouslySetInnerHTML={{
                            __html: message.content,
                        }}
                        style={{
                            ...typographyStyle(theme.typography.subtitle1),
                            whiteSpace: 'pre-line',
                        }}
                    />
                </Container>
            </Container>

            <Container style={{ flex: 1 }} />

            {isSecondary && (
                <Tooltip
                    title={
                        secondaryTitle ? secondaryTitle : 'From linked channel'
                    }
                >
                    <InfoIcon
                        style={{
                            alignSelf: 'flex-start',
                            justifySelf: 'flex-end',
                            color: theme.palette.primary.dark,
                        }}
                    />
                </Tooltip>
            )}
        </Container>
    )
}
